import { Image } from 'expo-image'
import { Upload, X } from 'lucide-react-native'
import { Pressable, StyleSheet, View } from 'react-native'
import { ActivityIndicator, IconButton, useTheme } from 'react-native-paper'
import { useColorScheme } from '@/hooks/useColorScheme'
import { useOrgLogo } from '@/hooks/useOrgLogo'
import { getOrgLogoUrl } from '@/lib/file-upload'

interface LogoUploadProps {
    orgId: string
    logo: string | null | undefined
    disabled?: boolean
}

export function LogoUpload({ orgId, logo, disabled }: LogoUploadProps) {
    const theme = useTheme()
    const colorScheme = useColorScheme()
    const { isUploading, isDeleting, pickAndUploadLogo, removeLogo } = useOrgLogo(orgId)
    const isDark = colorScheme === 'dark'

    const logoUrl = getOrgLogoUrl(orgId, logo)
    const isLoading = isUploading || isDeleting

    const previewBgColor =
        logoUrl && isDark ? 'rgba(255, 255, 255, 0.95)' : theme.colors.surfaceVariant

    return (
        <View style={styles.container}>
            <Pressable
                onPress={pickAndUploadLogo}
                disabled={disabled || isLoading}
                style={[styles.previewContainer, { backgroundColor: previewBgColor }]}
            >
                {logoUrl ? (
                    <Image
                        source={{ uri: logoUrl }}
                        style={styles.previewImage}
                        contentFit="contain"
                    />
                ) : (
                    <Upload size={24} color={theme.colors.onSurfaceVariant} />
                )}
                {isLoading && (
                    <View
                        style={[styles.loadingOverlay, { backgroundColor: theme.colors.backdrop }]}
                    >
                        <ActivityIndicator color={theme.colors.primary} size="small" />
                    </View>
                )}
            </Pressable>

            {logoUrl && (
                <IconButton
                    icon={() => <X size={16} color={theme.colors.error} />}
                    size={20}
                    onPress={removeLogo}
                    disabled={disabled || isLoading}
                    style={styles.removeButton}
                />
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    previewContainer: {
        width: 200,
        minHeight: 70,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        paddingHorizontal: 8,
    },
    previewImage: {
        width: '100%',
        minHeight: 70,
    },
    loadingOverlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
    },
    removeButton: {
        margin: 0,
    },
})
